import { useShows } from '../../api/hooks';
import type { CreateRightInput, RightType } from '../../api/types';
import {
  DateInput,
  FormField,
  MultiSelectChips,
  Select,
  TextInput,
} from '../../components/DesignSystem';
import { MEDIA_TYPE_OPTIONS } from './mediaTypes';

/** Editable form state — strings throughout; converted on submit. */
export interface RightDraft {
  showId: string;
  seasonId: string;
  trackId: string;
  rightType: RightType;
  category: string;
  mediaTypes: string[];
  territory: string;
  territoryTier: string;
  platform: string;
  device: string;
  tier: string;
  language: string;
  version: string;
  priority: string;
  sharePercent: string;
  licenseStart: string;
  licenseEnd: string;
  licenseFee: string;
  feePerEpisode: string;
  isExclusive: boolean;
  isHoldback: boolean;
  transmissionType: string;
  transmissionMethod: string;
  runType: string;
  runCount: string;
  runHold: string;
  sequentialRunTimeHours: string;
  exclusivityTerritory: string;
  exclusivityMarket: string;
  holdbackLimitDays: string;
  holdbackOffsetDays: string;
  holdbackScope: string;
  windowDependency: boolean;
  dependencyReason: string;
  windowAdjacency: string;
  ageAfterDependencyDays: string;
  territorySpecificDependency: string;
  isBackendGuaranteed: boolean;
  notes: string;
}

export const EMPTY_RIGHT_DRAFT: RightDraft = {
  showId: '',
  seasonId: '',
  trackId: '',
  rightType: 'primary',
  category: '',
  mediaTypes: [],
  territory: '',
  territoryTier: '',
  platform: '',
  device: '',
  tier: '',
  language: '',
  version: '',
  priority: '',
  sharePercent: '',
  licenseStart: '',
  licenseEnd: '',
  licenseFee: '',
  feePerEpisode: '',
  isExclusive: false,
  isHoldback: false,
  transmissionType: '',
  transmissionMethod: '',
  runType: '',
  runCount: '',
  runHold: '',
  sequentialRunTimeHours: '',
  exclusivityTerritory: '',
  exclusivityMarket: '',
  holdbackLimitDays: '',
  holdbackOffsetDays: '',
  holdbackScope: '',
  windowDependency: false,
  dependencyReason: '',
  windowAdjacency: '',
  ageAfterDependencyDays: '',
  territorySpecificDependency: '',
  isBackendGuaranteed: false,
  notes: '',
};

/** Validates and converts a draft into the API input; null when invalid. */
export function draftToInput(draft: RightDraft): CreateRightInput | null {
  if (draft.showId === '' || draft.mediaTypes.length === 0 || draft.territory.trim() === '') {
    return null;
  }

  const optionalNumber = (value: string) => (value.trim() === '' ? undefined : Number(value));
  const optionalString = (value: string) => (value.trim() === '' ? undefined : value.trim());

  return {
    showId: draft.showId,
    seasonId: optionalString(draft.seasonId),
    trackId: optionalString(draft.trackId),
    rightType: draft.rightType,
    category: optionalString(draft.category),
    mediaTypes: draft.mediaTypes,
    territory: draft.territory.trim(),
    territoryTier: optionalString(draft.territoryTier),
    platform: optionalString(draft.platform),
    device: optionalString(draft.device),
    tier: optionalString(draft.tier),
    language: optionalString(draft.language),
    version: optionalString(draft.version),
    priority: optionalNumber(draft.priority),
    sharePercent: optionalNumber(draft.sharePercent),
    licenseStart: optionalString(draft.licenseStart),
    licenseEnd: optionalString(draft.licenseEnd),
    licenseFee: optionalNumber(draft.licenseFee),
    feePerEpisode: optionalNumber(draft.feePerEpisode),
    isExclusive: draft.isExclusive,
    isHoldback: draft.isHoldback,
    transmissionType: optionalString(draft.transmissionType),
    transmissionMethod: optionalString(draft.transmissionMethod),
    runType: optionalString(draft.runType),
    runCount: optionalNumber(draft.runCount),
    runHold: optionalNumber(draft.runHold),
    sequentialRunTimeHours: optionalNumber(draft.sequentialRunTimeHours),
    exclusivityTerritory: optionalString(draft.exclusivityTerritory),
    exclusivityMarket: optionalString(draft.exclusivityMarket),
    holdbackLimitDays: optionalNumber(draft.holdbackLimitDays),
    holdbackOffsetDays: optionalNumber(draft.holdbackOffsetDays),
    holdbackScope: optionalString(draft.holdbackScope),
    windowDependency: draft.windowDependency,
    dependencyReason: optionalString(draft.dependencyReason),
    windowAdjacency: optionalString(draft.windowAdjacency),
    ageAfterDependencyDays: optionalNumber(draft.ageAfterDependencyDays),
    territorySpecificDependency: optionalString(draft.territorySpecificDependency),
    isBackendGuaranteed: draft.isBackendGuaranteed,
    notes: optionalString(draft.notes),
  };
}

interface RightFormProps {
  idPrefix: string;
  draft: RightDraft;
  onChange: (draft: RightDraft) => void;
  /** When set, the Show/Season selects are hidden — the caller already knows them (e.g. the upload wizard). */
  lockedShowId?: string;
  lockedSeasonId?: string;
}

/** Every create-right field, grouped into labeled sections: Core, Distribution Constraints, Transmission & Runs, Exclusivity Scope, Holdback & Window Dependency, Notes. */
export function RightForm({
  idPrefix,
  draft,
  onChange,
  lockedShowId,
  lockedSeasonId,
}: RightFormProps) {
  const shows = useShows({ limit: 100 });
  const showList = shows.data?.data ?? [];
  const selectedShow = showList.find((show) => show.id === draft.showId);
  const isShowLocked = lockedShowId !== undefined;
  const isSeasonLocked = lockedSeasonId !== undefined;

  const set = (patch: Partial<RightDraft>) => onChange({ ...draft, ...patch });

  const text = (key: keyof RightDraft, label: string, placeholder = '') => (
    <FormField htmlFor={`${idPrefix}-${key}`} label={label}>
      <TextInput
        id={`${idPrefix}-${key}`}
        onChange={(event) => set({ [key]: event.target.value } as Partial<RightDraft>)}
        placeholder={placeholder}
        value={String(draft[key])}
      />
    </FormField>
  );

  const numberField = (key: keyof RightDraft, label: string, placeholder = '') => (
    <FormField htmlFor={`${idPrefix}-${key}`} label={label}>
      <TextInput
        id={`${idPrefix}-${key}`}
        min="0"
        onChange={(event) => set({ [key]: event.target.value } as Partial<RightDraft>)}
        placeholder={placeholder}
        type="number"
        value={String(draft[key])}
      />
    </FormField>
  );

  const checkbox = (key: 'windowDependency' | 'isBackendGuaranteed', label: string) => (
    <label className="rfp-check">
      <input
        checked={draft[key]}
        id={`${idPrefix}-${key}`}
        onChange={(event) => set({ [key]: event.target.checked })}
        type="checkbox"
      />
      {label}
    </label>
  );

  return (
    <div className="rfp-stack">
      <p className="rfp-card__caption">Core</p>
      <div className="rfp-form-grid">
        {!isShowLocked && (
          <FormField htmlFor={`${idPrefix}-show`} label="Show (required)">
            <Select
              id={`${idPrefix}-show`}
              onChange={(event) => set({ showId: event.target.value, seasonId: '' })}
              value={draft.showId}
            >
              <option value="">Select a show…</option>
              {showList.map((show) => (
                <option key={show.id} value={show.id}>
                  {show.name}
                </option>
              ))}
            </Select>
          </FormField>
        )}

        {!isSeasonLocked && (
          <FormField htmlFor={`${idPrefix}-season`} label="Season">
            <Select
              id={`${idPrefix}-season`}
              onChange={(event) => set({ seasonId: event.target.value })}
              value={draft.seasonId}
            >
              <option value="">All seasons</option>
              {(selectedShow?.seasons ?? []).map((season) => (
                <option key={season.id} value={season.id}>
                  {season.seasonCode}
                </option>
              ))}
            </Select>
          </FormField>
        )}

        <FormField htmlFor={`${idPrefix}-rightType`} label="Type">
          <Select
            id={`${idPrefix}-rightType`}
            onChange={(event) => set({ rightType: event.target.value as RightType })}
            value={draft.rightType}
          >
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
          </Select>
        </FormField>

        <FormField htmlFor={`${idPrefix}-mediaTypes`} label="Media Type (required)">
          <MultiSelectChips
            id={`${idPrefix}-mediaTypes`}
            onChange={(mediaTypes) => set({ mediaTypes })}
            options={MEDIA_TYPE_OPTIONS}
            value={draft.mediaTypes}
          />
        </FormField>

        {text('category', 'Category', 'e.g. broadcast')}
        {numberField('sharePercent', 'Share %')}
        {text('territory', 'Territory (required)', 'e.g. UK')}

        <label className="rfp-check">
          <input
            checked={draft.isExclusive}
            id={`${idPrefix}-isExclusive`}
            onChange={(event) => set({ isExclusive: event.target.checked })}
            type="checkbox"
          />
          Exclusive
        </label>

        <FormField htmlFor={`${idPrefix}-licenseStart`} label="License Start">
          <DateInput
            id={`${idPrefix}-licenseStart`}
            onChange={(event) => set({ licenseStart: event.target.value })}
            value={draft.licenseStart}
          />
        </FormField>

        <FormField htmlFor={`${idPrefix}-licenseEnd`} label="License End">
          <DateInput
            id={`${idPrefix}-licenseEnd`}
            onChange={(event) => set({ licenseEnd: event.target.value })}
            value={draft.licenseEnd}
          />
        </FormField>

        {numberField('priority', 'Priority')}
        {text('trackId', 'Track ID')}
      </div>

      <hr className="rfp-divider" />
      <p className="rfp-card__caption">Distribution Constraints</p>
      <div className="rfp-form-grid">
        {text('territoryTier', 'Territory Tier')}
        {text('platform', 'Platform', 'e.g. Netflix')}
        {text('device', 'Device')}
        {text('language', 'Language', 'e.g. en')}
        {text('version', 'Version')}
        {text('licenseFee', 'License Fee £')}
        {text('feePerEpisode', 'Fee / Episode £')}
      </div>

      <hr className="rfp-divider" />
      <p className="rfp-card__caption">Transmission &amp; Runs</p>
      <div className="rfp-form-grid">
        {text('transmissionType', 'Transmission Type', 'e.g. Live')}
        {text('transmissionMethod', 'Transmission Method', 'e.g. OTT')}
        {text('runType', 'Run Type')}
        {numberField('runCount', 'Run Count')}
        {numberField('runHold', 'Run/Hold')}
        {numberField('sequentialRunTimeHours', 'Sequential Run Time (hrs)')}
      </div>

      <hr className="rfp-divider" />
      <p className="rfp-card__caption">Exclusivity Scope</p>
      <div className="rfp-form-grid">
        {text('exclusivityTerritory', 'Exclusivity Territory/Sector')}
        {text('exclusivityMarket', 'Exclusivity Scope Market')}
      </div>

      <hr className="rfp-divider" />
      <p className="rfp-card__caption">Holdback &amp; Window Dependency</p>
      <div className="rfp-form-grid">
        <label className="rfp-check">
          <input
            checked={draft.isHoldback}
            id={`${idPrefix}-isHoldback`}
            onChange={(event) => set({ isHoldback: event.target.checked })}
            type="checkbox"
          />
          Holdback
        </label>

        {numberField('holdbackLimitDays', 'Holdback Limit (days)')}
        {numberField('holdbackOffsetDays', 'Holdback Offset (days)')}
        {text('holdbackScope', 'Holdback Scope')}
        {checkbox('windowDependency', 'Window Dependency')}
        {text('dependencyReason', 'Dependency Reason')}
        {text('windowAdjacency', 'Window Adjacency', 'e.g. Same after dependency ends')}
        {numberField('ageAfterDependencyDays', 'Age After Dependency (days)')}
        {text('territorySpecificDependency', 'Territory Specific Dependency')}
        {checkbox('isBackendGuaranteed', 'Backend Guaranteed')}
      </div>

      <hr className="rfp-divider" />
      {text('notes', 'Notes')}
    </div>
  );
}
